import { useState } from 'react';
import cx from 'classnames';
import {
    FGTypography,
    FGIcons,
    FGButton,
    FGTooltip,
    FGToastDismiss,
    TOOLTIP_PLACEMENT,
} from '@fg/design-system';
import ChartExportModal, { EXPORT_CHART_MODAL_KEY } from './ChartExportModal';
import GQPopupStorage from '../GQPopup/GQPopupStorage';
import {
    createFileAndDownload,
    generateExportPayload,
} from '../GQTemplateV2/exportUtils';
import {
    CHART_EXPORT_FILE_TYPES,
} from '../../constants';
import { BILATERAL_RISK_VIEWS, IExportChartConfig, VIEW } from 'interfaces';
import { createExportErrorToaster, createExportStartToaster } from './ExportChartToaster';
import { getCurrentActiveView } from 'utils/generalUtils';
import { trackCustomEvent } from 'utils/piwikUtils';
import { getCategoryForPiwik } from 'PageTitles';


const EXPORT_BUTTON_INSTRUCTIONS =
    'Select at least one country to enable export';
const EXPORT_BILATERAL_BUTTON_INSTRUCTIONS =
    'Select primary country and at least one comparison country to enable export';
const EXPORT_IN_PROGRESS_MESSAGE = 'Has export in progress';

const modalBtnClassPrefix = 'export-chart-open-modal-btn';

type Props = {
    hasCountriesSelected: boolean;
}

const ChartExportButton = ({ hasCountriesSelected }: Props) => {

    const [isExportInProgress, setIsExportInProgress] = useState(false);
    const activeView = getCurrentActiveView();

    const isBilateralView = BILATERAL_RISK_VIEWS.includes(activeView);

    let exportButtonEnabled: boolean;
    switch (activeView) {
        case VIEW.COUNTRY_GRAPH:
        case VIEW.COUNTRY_GRAPH_SELECT_COUNTRY:
        case VIEW.CONTINGENT_GRAPH:
        case VIEW.WORLD_GRAPH:
        case VIEW.SORT_BY_CONTINGENT_RISK:
        case VIEW.SORT_BY_CONTINGENT_DELTA:
            exportButtonEnabled = hasCountriesSelected && !isExportInProgress;
            break;
        default:
            exportButtonEnabled = !isExportInProgress;
    }

    let tooltipMessage = null;
    if (!exportButtonEnabled) {
        tooltipMessage = !hasCountriesSelected
            ? (isBilateralView ? EXPORT_BILATERAL_BUTTON_INSTRUCTIONS : EXPORT_BUTTON_INSTRUCTIONS)
            : EXPORT_IN_PROGRESS_MESSAGE;
    }
    

    const onExportClicked = async (selectedType: CHART_EXPORT_FILE_TYPES) => {
        GQPopupStorage.addData(EXPORT_CHART_MODAL_KEY, null);
        setIsExportInProgress(true);
        
        const chartPayload: IExportChartConfig = generateExportPayload()
        
        const exportStartToastId = createExportStartToaster();

        try {
            await createFileAndDownload(
                selectedType,
                chartPayload,
            );
        } catch (e) {
            createExportErrorToaster();
            console.error(e);
        } finally {
            FGToastDismiss(exportStartToastId);
            setIsExportInProgress(false);
        }
    };

    const onExportButtonClicked = () => {
        const category = getCategoryForPiwik(activeView);
        trackCustomEvent(`Geo Risk Signals ${category}`, 'Export Data', `${category} Export Data Download`, 0);
        GQPopupStorage.addData(EXPORT_CHART_MODAL_KEY, {
            element: document.getElementById('export-chart-button'),
            tooltipData: (
                <ChartExportModal onExportButtonClicked={onExportClicked} />
            ),
        });
    };

    const ButtonComponent = (
        <FGButton
            disabled={!exportButtonEnabled}
            id="export-chart-button"
            className={cx([
                modalBtnClassPrefix,
                ...(exportButtonEnabled
                    ? [`${modalBtnClassPrefix}-enabled`]
                    : [`${modalBtnClassPrefix}-disabled`]),
            ])}
            icon={
                <FGIcons
                    icon="Download"
                    className={cx([
                        `${modalBtnClassPrefix}-text`,
                        ...(exportButtonEnabled
                            ? [`${modalBtnClassPrefix}-text-enabled`]
                            : [`${modalBtnClassPrefix}-text-disabled`]),
                    ])}
                />
            }
            onClick={onExportButtonClicked}>
            <FGTypography
                tag="label"
                variant="label2"
                cssStyle={{ whiteSpace: 'nowrap' }}
                className={cx([
                    `${modalBtnClassPrefix}-text`,
                    ...(exportButtonEnabled
                        ? [`${modalBtnClassPrefix}-text-enabled`]
                        : [`${modalBtnClassPrefix}-text-disabled`]),
                ])}>
                Export data
            </FGTypography>
        </FGButton>
    );

    // When wrapping the button with FGTooltip component, it will always render it on hover, even if the content is empty.
    // So only wrap the button if there is a tooltip message.
    return tooltipMessage ? (
        <FGTooltip
            appendTo="parent"
            placement={TOOLTIP_PLACEMENT.top}
            className="export-chart-tooltip"
            content={tooltipMessage}>
            {/* div wrapper needed so tooltip hover works */}
            <div>{ButtonComponent}</div>
        </FGTooltip>
    ) : (
        ButtonComponent
    );
};

export default ChartExportButton;
